import { useContext, useEffect, useState } from 'react'
import { Select, TextField } from '@nv/basic-components'
import { ModalContext } from '@nv/contexts'
import { CheckAnimationIcon } from '@nv/images/design/check-animation-icon'
import { XCircleAnimatedIcon } from '@nv/images/design/x-circle-animated-icon'
import { WorkerProxy, WorkerProxyConf } from '@tivi/core-ts'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import styles from './VerifyVoterReceipt.module.scss'

export const VerifyVoterReceipt = ({ event, userVoteReceiptEvent }) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [status, setStatus] = useState('idle')
  const [workerProxy, setWorkerProxy] = useState<WorkerProxy | null>(null)
  const { setHeaderLabel, setBtns, onClose } = useContext(ModalContext)

  const formik = useFormik({
    initialValues: {
      code: '',
      contest: event?.questions?.find((q) => q?.qid === userVoteReceiptEvent?.qid)?.qid || '',
    },
    validationSchema: Yup.object({
      code: Yup.string().required(t('events.voteReceipt.codeRequired')),
      contest: Yup.string().required(t('events.voteReceipt.contestRequired')),
    }),
    onSubmit: () => checkReceipt(),
  })

  useEffect(() => {
    try {
      const workerProxyConf: WorkerProxyConf = {
        workerLocation: './workers/worker.js',
        oneKeyIdURL: '',
        oneKeyAuthURL: '',
        clsURLTemplate: '',
        pbbURLTemplate: '',
        randomURL: '',
        sessionURL: '',
        submitURLTemplate: '',
        elections: {},
      }
      const newWorkerProxy = new WorkerProxy(workerProxyConf)
      setWorkerProxy(newWorkerProxy)
    } catch (error) {
      console.error(error)
    }
  }, [])

  async function checkReceipt() {
    try {
      const verifyURL =
        event?.questions && event?.questions.length
          ? event?.questions?.find((q) => q?.qid === formik.values.contest)?.voteVerifyLink
          : event?.voteVerifyLink
      const data = await workerProxy.isVoteVerified(formik.values.code, verifyURL)
      if (data) {
        setStatus('valid')
        return true
      }
      return false
    } catch (err) {
      setStatus('invalid')
      console.log('error validating', err)
      return false
    }
  }

  useEffect(() => {
    setHeaderLabel(status === 'idle' ? t('events.voteReceipt.verifyVoteReceipt') : t('events.voteVerification.votingVerification'))

    setBtns([
      ...(status === 'valid'
        ? [{ text: t('buttons.ok'), onClick: onClose }]
        : [{ text: t('buttons.cancel'), onClick: onClose, variant: 'cancel' }]),
      ...(status === 'idle' ? [{ text: t('buttons.verify'), onClick: formik.handleSubmit }] : []),
      ...(status === 'invalid' ? [{ text: t('buttons.tryAgain'), onClick: () => setStatus('idle') }] : []),
    ])
  }, [status])

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className={styles['container']}>
        {status === 'idle' ? (
          <>
            <h2>{t('events.voteReceipt.enterReceiptCode')}</h2>
            <Select
              label={t('events.voteVerification.contestSelect')}
              formik={formik}
              field='contest'
              value={formik.values.contest}
              options={
                event.questions?.map((question, index) => ({
                  value: question.qid,
                  label: question.texts?.[currentLanguage] || `Contest ${index + 1}`,
                })) || []
              }
              isRequired={true}
            />

            <TextField
              formik={formik}
              field='code'
              placeholder={t('events.voteReceipt.verificationCode')}
              label={t('events.voteReceipt.verificationCode')}
              isRequired={true}
            />
          </>
        ) : (
          <>
            <h2 className={styles['label']}>{t('events.voteReceipt.verificationCode')}</h2>
            <div className={styles['code-container']}>
              <div className={styles['code']}>{formik.values.code}</div>
              <div className={status === 'valid' ? styles['valid'] : styles['invalid']}>
                {status === 'valid' ? t('events.voteReceipt.valid') : t('events.voteReceipt.inValid')}
              </div>
            </div>

            <h2 className={styles['label']}>{t('events.voteReceipt.votingStatus')}</h2>
            <div className={styles['response__container']}>
              {status === 'valid' ? (
                <>
                  <div className={styles['info']}>
                    <CheckAnimationIcon />
                    <div className={styles['note']}>{t('events.voteReceipt.providedCodeCorrect')}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles['info']}>
                    <XCircleAnimatedIcon />
                    <div className={styles['note']}>{t('events.voteReceipt.providedCodeIncorrect')}</div>
                  </div>
                  <div>{t('events.voteReceipt.providedCodeInfo')}</div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </form>
  )
}
